import { IUser } from './../../common/types/User'
import { intersectRoles } from './../helpers/auth-helpers'
import { UserRole } from './../../common/types/Irmao'
import { TokenPayload } from './../../common/types/Token'
import { logar, logarPeloCim, loginFirebase, logout as serviceLogout, registrar } from '@/services/AuthService'
import { defineStore } from 'pinia'
import Credenciais, { InputLoginCim } from 'common/types/Credenciais'
import itensMenu from '@/shareds/itens-menu'
import { userModule, application } from '@/store'
import { auth } from '@/firebase'
import { Cadastro } from 'common/types/Cadastro'
import Login, { AcessoParcial } from 'common/types/Login'
import { queryClient } from '@/plugins/tanstack'
import { jwtDecode } from 'jwt-decode'
import { éRoleDeVisitante, éVisitante } from '../../common/shareds/sharedAuth'


function mensagemDeErroDoLogin(error: { code?: string, message?: string }) {
	switch (error.code) {
		case 'auth/wrong-password':
		case 'auth/user-not-found':
		case 'auth/invalid-email':
		case 'auth/invalid-credential':
		case 'auth/invalid-login-credentials':
			return 'E-mail ou senha incorretos'
		case 'auth/too-many-requests':
			return 'Muitas tentativas. Tente novamente mais tarde ou redefina sua senha.'
		case 'auth/user-disabled':
			return 'Usuário desabilitado.'
	}
	return error.message || 'Não foi possível entrar'
}

const ID_USUARIO_STORAGE = 'idUsuario'
const TOKEN_STORAGE = 'token'
const LOCAL_ROLE = 'role'
const É_ADMIN_STORAGE = 'éAdmin'

/** papel salvo por versões antigas sem JSON é regravado como JSON */
function lerRoleDoLocalStorage(): UserRole | UserRole[] | null {
	if (!localStorage[LOCAL_ROLE]) return null
	try {
		return JSON.parse(localStorage[LOCAL_ROLE])
	} catch {
		localStorage[LOCAL_ROLE] = JSON.stringify(localStorage[LOCAL_ROLE])
		return JSON.parse(localStorage[LOCAL_ROLE])
	}
}

export const useAuthStore = defineStore('auth', {
	state: () => ({
		token: (localStorage.getItem(TOKEN_STORAGE)
			? JSON.parse(localStorage.token)
			: null) as string | null,
		idUsuario: (localStorage.getItem(ID_USUARIO_STORAGE)
			? JSON.parse(localStorage.idUsuario)
			: null) as string | null,
		role: lerRoleDoLocalStorage(),
		éAdmin: (localStorage[É_ADMIN_STORAGE] as boolean || false) satisfies boolean,
		usuarioTemporario: null as AcessoParcial | null,
		/** e-mail associado que ainda aguarda a confirmação pelo link; lembrado só logo após o login */
		emailPendente: null as string | null,
		sugerirEmail: false,
	}),

	actions: {
		setToken(token: string | null) {
			token
				? localStorage.token = JSON.stringify(token)
				: localStorage.removeItem(TOKEN_STORAGE)
			this.token = token
		},

		setRole(role: UserRole | UserRole[] | null) {
			role
				? localStorage.setItem(LOCAL_ROLE, JSON.stringify(role))
				: localStorage.removeItem(LOCAL_ROLE)
			this.role = role
		},

		setIdUsuario(idUsuario: string | null) {
			idUsuario
				? localStorage.idUsuario = JSON.stringify(idUsuario)
				: localStorage.removeItem(ID_USUARIO_STORAGE)
			this.idUsuario = idUsuario
		},

		setÉAdmin(éAdmin: boolean | undefined) {
			éAdmin
				? localStorage[É_ADMIN_STORAGE] = JSON.stringify(éAdmin)
				: localStorage.removeItem(É_ADMIN_STORAGE)
			this.éAdmin = éAdmin || false
		},

		setUsuarioTemporario(usuario: AcessoParcial | null) {
			this.usuarioTemporario = usuario
		},

		limparSessao() {
			this.setRole(null)
			this.setIdUsuario(null)
			this.setUsuarioTemporario(null)
			this.emailPendente = null
			this.sugerirEmail = false
			userModule.setCurrentUser(null)
			queryClient.clear()
		},

		setUsuario(usuario: IUser) {
			this.setIdUsuario(usuario._id || '')
			this.setRole(usuario.role)
		},

		async logar(credenciais: Credenciais) {
			const { data } = await logar(credenciais)

			return this.logarToken(data)
		},

		async logarPeloCim(credenciais: InputLoginCim) {
			const { data } = await logarPeloCim(credenciais)

			return this.logarToken(data)
		},

		/** a senha é conferida pelo Firebase; o servidor troca o ID token pelo token do app e a guarda para o login pelo CIM */
		async logarPeloEmail(email: string, senha: string) {
			const { user } = await auth.signInWithEmailAndPassword(email, senha)
				.catch((error) => { throw new Error(mensagemDeErroDoLogin(error)) })
			const { data } = await loginFirebase({ idToken: await user!.getIdToken(), senha })

			return this.logarToken(data)
		},

		async logarToken(login: Login) {
			await auth.signInAnonymously()
				.catch((error) => {
					if (process.env.NODE_ENV !== 'development') throw error
				})

			// nada da sessão anterior sobrevive ao novo login (nem no acesso parcial dos termos, senha e palavra)
			this.limparSessao()
			this.setToken(login.token)
			this.setÉAdmin(login.éAdmin)
			this.emailPendente = login.emailPendente ?? null
			this.sugerirEmail = !!login.sugerirEmail

			if ('_id' in login.usuario) {
				this.setUsuario(login.usuario)
				userModule.setCurrentUser(login.usuario)
				await application.inicializarAtualizacaoDeContexto()
			} else {
				this.setUsuarioTemporario(login.usuario)
			}

			return login
		},

		async registrar(cadastro: Cadastro) {
			await auth.signInAnonymously()
				.catch((error) => {
					if (process.env.NODE_ENV !== 'development') throw error
				})

			const { data } = await registrar({
				...cadastro,
				ultimaAtualizacao: new Date().toISOString(),
			})
			if (!('_id' in data.usuario)) throw new Error('Acesso Parcial inesperado')

			this.limparSessao()
			this.setUsuario(data.usuario)
			this.setToken(data.token)
			this.setÉAdmin(data.éAdmin)
			userModule.setCurrentUser(data.usuario)
			await application.inicializarAtualizacaoDeContexto()

			return data
		},

		async logout() {
			this.setToken(null)
			this.setIdUsuario(null)
			this.setRole(null)
			userModule.setCurrentUser(null)
			application.pausarAtualizacaoDeContexto()
			queryClient.clear()
			localStorage.clear()
			await serviceLogout()
			await auth.signOut()
			globalThis.location.reload()
		},
	},

	getters: {
		activeMenus(): typeof itensMenu {
			if (!this.isAuth) return []
			return itensMenu
				.filter((item) => this.éVisitante
					? item.permiteVisitante
					: !item.requires || intersectRoles(this.role, item.requires))
		},

		podeAbrirPerfil(): (idIrmao: string) => boolean {
			return (idIrmao) => !this.éVisitante || idIrmao === 'me' || idIrmao === userModule.currentUser?._id
		},

		éVisitante(): boolean {
			return éRoleDeVisitante(this.role) || éVisitante(userModule.currentUser)
		},

		hasAuthorization(): (requires: (UserRole | UserRole[] | ((user: IUser) => boolean))[]) => boolean {
			return (requires) => {
				if (!requires.length) return true
				return requires.some((requirement) => {
					return typeof requirement !== 'function'
						? !!this.role && intersectRoles(requirement, this.role)
						: !!userModule.currentUser && requirement(userModule.currentUser)
				})
			}
		},

		isAuth(): boolean {
			return !!this.token && !this.restricao
		},

		jwtPayload(): TokenPayload | null {
			if (!this.token) return null;
			try {
				const decoded = jwtDecode<TokenPayload>(this.token)
				return decoded
			} catch (error) {
				console.error('Erro ao parse do jwt', error)
				return null;
			}
		},

		restricao(): TokenPayload['restricao'] {
			return this.jwtPayload?.restricao
		},
	},
})
